import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, User, Building, AlertTriangle, FileText, Download, Eye, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

interface KycVerification {
  id: string;
  email: string;
  mobile: string;
  verification_type: 'individual' | 'company';
  status: 'pending' | 'approved' | 'rejected';
  verification_data: any;
  submitted_at: string;
  verified_at: string | null;
  rejection_reason: string | null;
  document_count: number;
}

interface KycDocument {
  id: string;
  verification_id: string;
  document_type: string;
  file_path: string;
  uploaded_at: string;
}

export default function AdminKycDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [verification, setVerification] = useState<KycVerification | null>(null);
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (id) {
      fetchVerificationDetail();
    }
  }, [id]);

  const fetchVerificationDetail = async () => {
    try {
      const token = localStorage.getItem('authToken');
      
      // Fetch verification details
      const verificationResponse = await fetch(`${API_BASE}/api/admin/kyc/verification/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!verificationResponse.ok) {
        throw new Error('Failed to fetch verification details');
      }

      const verificationData = await verificationResponse.json();
      setVerification(verificationData.data);

      // Fetch documents
      const documentsResponse = await fetch(`${API_BASE}/api/admin/kyc/verification/${id}/documents`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (documentsResponse.ok) {
        const documentsData = await documentsResponse.json();
        setDocuments(documentsData.data || []);
      }

    } catch (error) {
      console.error('Error fetching verification detail:', error);
      toast.error('ไม่สามารถดึงข้อมูลรายละเอียดได้');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!verification) return;
    
    setActionLoading('approve');
    try {
      const token = localStorage.getItem('authToken');
      const endpoint = verification.verification_type === 'individual' 
        ? '/api/admin/kyc/individual/approve'
        : '/api/admin/kyc/company/approve';

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ verificationId: verification.id })
      });

      if (!response.ok) {
        throw new Error('Failed to approve verification');
      }

      toast.success('อนุมัติการยืนยันตัวตนสำเร็จ');
      fetchVerificationDetail(); // Refresh data
    } catch (error) {
      console.error('Error approving verification:', error);
      toast.error('เกิดข้อผิดพลาดในการอนุมัติ');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!verification || !rejectionReason.trim()) return;
    
    setActionLoading('reject');
    try {
      const token = localStorage.getItem('authToken');
      const endpoint = verification.verification_type === 'individual' 
        ? '/api/admin/kyc/individual/reject'
        : '/api/admin/kyc/company/reject';

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          verificationId: verification.id,
          rejectionReason: rejectionReason.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to reject verification');
      }

      toast.success('ปฏิเสธการยืนยันตัวตนสำเร็จ');
      setShowRejectDialog(false);
      setRejectionReason('');
      fetchVerificationDetail(); // Refresh data
    } catch (error) {
      console.error('Error rejecting verification:', error);
      toast.error('เกิดข้อผิดพลาดในการปฏิเสธ');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-600 border-yellow-200">รอดำเนินการ</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">อนุมัติแล้ว</Badge>;
      case 'rejected':
        return <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">ปฏิเสธแล้ว</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getDocumentTypeName = (docType: string): string => {
    const typeMap: { [key: string]: string } = {
      'id_card_front': 'บัตรประชาชน (ด้านหน้า)',
      'id_card_back': 'บัตรประชาชน (ด้านหลัง)',
      'house_registration': 'ทะเบียนบ้าน',
      'company_registration': 'หนังสือจดทะเบียนบริษัท',
      'director_id_card': 'บัตรประชาชนกรรมการ',
      'company_affidavit': 'หนังสือรับรองบริษัท',
      'vat_registration': 'ทะเบียนภาษีมูลค่าเพิ่ม (ภ.ภ.20)',
      'additional_document': 'เอกสารเพิ่มเติม',
      'bank_statement': 'สำเนาบัญชีธนาคาร',
      'power_of_attorney': 'หนังสือมอบอำนาจ'
    };
    
    return typeMap[docType] || docType;
  };

  const getDocumentIcon = (_docType: string) => {
    return <FileText className="w-5 h-5 text-blue-600" />;
  };

  const handleDocumentView = (doc: KycDocument) => {
    // Open document in new tab with admin token
    const token = localStorage.getItem('authToken');
    const viewUrl = `${API_BASE}/api/admin/kyc/documents/view/${doc.id}?token=${encodeURIComponent(token || '')}`;
    window.open(viewUrl, '_blank');
  };

  const handleDocumentDownload = (doc: KycDocument) => {
    // Download document with admin token
    const token = localStorage.getItem('authToken');
    const downloadUrl = `${API_BASE}/api/admin/kyc/documents/download/${doc.id}?token=${encodeURIComponent(token || '')}`;
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${doc.document_type}_${doc.id}`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  if (!verification) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">ไม่พบข้อมูล</h2>
          <p className="text-gray-600 mb-4">ไม่สามารถโหลดข้อมูลการยืนยันตัวตนได้</p>
          <Button onClick={() => navigate('/admin/kyc')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            กลับหน้าหลัก
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/admin/kyc')}
                className="flex items-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                กลับ
              </Button>
              <div className="flex items-center space-x-3">
                {verification.verification_type === 'individual' ? (
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Building className="w-6 h-6 text-purple-600" />
                  </div>
                )}
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">
                    รายละเอียดการยืนยันตัวตน
                  </h1>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-sm text-gray-500">
                      {verification.verification_type === 'individual' ? 'บุคคลธรรมดา' : 'นิติบุคคล'}
                    </span>
                    {getStatusBadge(verification.status)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Action Buttons */}
            {verification.status === 'pending' && (
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowRejectDialog(true)}
                  disabled={!!actionLoading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  {actionLoading === 'reject' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2"></div>
                  ) : (
                    <X className="w-4 h-4 mr-2" />
                  )}
                  ปฏิเสธ
                </Button>
                <Button 
                  onClick={handleApprove}
                  disabled={!!actionLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {actionLoading === 'approve' ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2"></div>
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  อนุมัติ
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 text-xs">📞</span>
                  </div>
                  ข้อมูลติดต่อ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600 font-medium">อีเมล</Label>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded border">{verification.email}</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600 font-medium">เบอร์โทรศัพท์</Label>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded border">{verification.mobile}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Verification Details */}
            {verification.verification_data && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-xs">📋</span>
                    </div>
                    ข้อมูลการยืนยัน
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {verification.verification_type === 'individual' ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">คำนำหน้า</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border">
                            {verification.verification_data.prefix || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">ชื่อ</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border">
                            {verification.verification_data.firstName || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">นามสกุล</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border">
                            {verification.verification_data.lastName || '-'}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600 font-medium">เลขบัตรประชาชน</Label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded border font-mono">
                          {verification.verification_data.idCard || '-'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600 font-medium">ที่อยู่</Label>
                        <p className="text-gray-900 bg-gray-50 p-4 rounded border leading-relaxed">
                          {verification.verification_data.address || '-'}
                        </p>
                      </div>

                      {/* Province / District / Subdistrict / Postal */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">จังหวัด</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                            {verification.verification_data.province || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">เขต / อำเภอ</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                            {verification.verification_data.subDistrict || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">แขวง / ตำบล</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                            {verification.verification_data.district || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">รหัสไปรษณีย์</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm font-mono">
                            {verification.verification_data.postalCode || '-'}
                          </p>
                        </div>
                      </div>

                      {/* Tax Invoice Delivery Address */}
                      {verification.verification_data.useAddressForTax === false && (
                        <div className="space-y-3 border-t border-orange-200 pt-4 mt-2">
                          <p className="text-sm font-semibold text-orange-700">📦 ที่อยู่จัดส่งใบกำกับภาษี (ใช้ที่อยู่ต่างจากที่อยู่หลัก)</p>
                          <div className="space-y-2">
                            <Label className="text-sm text-gray-600 font-medium">ที่อยู่</Label>
                            <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200">
                              {verification.verification_data.taxInvoiceDeliveryAddress || '-'}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">จังหวัด</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm">
                                {verification.verification_data.taxInvoiceDeliveryProvince || '-'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">เขต / อำเภอ</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm">
                                {verification.verification_data.taxInvoiceDeliverySubDistrict || '-'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">แขวง / ตำบล</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm">
                                {verification.verification_data.taxInvoiceDeliveryDistrict || '-'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">รหัสไปรษณีย์</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm font-mono">
                                {verification.verification_data.taxInvoiceDeliveryPostalCode || '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600 font-medium">ชื่อบริษัท</Label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded border">
                          {verification.verification_data.companyName || '-'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600 font-medium">เลขประจำตัวผู้เสียภาษี</Label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded border font-mono">
                          {verification.verification_data.taxId || '-'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600 font-medium">ผู้ติดต่อ</Label>
                        <p className="text-gray-900 bg-gray-50 p-3 rounded border">
                          {verification.verification_data.contactPerson || '-'}
                        </p>
                      </div>

                      {/* Address */}
                      <div className="space-y-2">
                        <Label className="text-sm text-gray-600 font-medium">ที่อยู่</Label>
                        <p className="text-gray-900 bg-gray-50 p-4 rounded border leading-relaxed">
                          {verification.verification_data.address || '-'}
                        </p>
                      </div>

                      {/* Province / District / Subdistrict / Postal */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">จังหวัด</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                            {verification.verification_data.province || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">เขต / อำเภอ</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                            {verification.verification_data.subDistrict || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">แขวง / ตำบล</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                            {verification.verification_data.district || '-'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm text-gray-600 font-medium">รหัสไปรษณีย์</Label>
                          <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm font-mono">
                            {verification.verification_data.postalCode || '-'}
                          </p>
                        </div>
                      </div>

                      {/* Tax Invoice Delivery Address */}
                      {verification.verification_data.useAddressForTax === false && (
                        <div className="space-y-3 border-t border-orange-200 pt-4 mt-2">
                          <p className="text-sm font-semibold text-orange-700">📦 ที่อยู่จัดส่งใบกำกับภาษี (ใช้ที่อยู่ต่างจากที่อยู่หลัก)</p>
                          <div className="space-y-2">
                            <Label className="text-sm text-gray-600 font-medium">ที่อยู่</Label>
                            <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200">
                              {verification.verification_data.taxInvoiceDeliveryAddress || '-'}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">จังหวัด</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm">
                                {verification.verification_data.taxInvoiceDeliveryProvince || '-'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">เขต / อำเภอ</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm">
                                {verification.verification_data.taxInvoiceDeliverySubDistrict || '-'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">แขวง / ตำบล</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm">
                                {verification.verification_data.taxInvoiceDeliveryDistrict || '-'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm text-gray-600 font-medium">รหัสไปรษณีย์</Label>
                              <p className="text-gray-900 bg-orange-50 p-3 rounded border border-orange-200 text-sm font-mono">
                                {verification.verification_data.taxInvoiceDeliveryPostalCode || '-'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                    <FileText className="w-3 h-3 text-purple-600" />
                  </div>
                  เอกสารแนบ ({documents.length} ไฟล์)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {documents.length > 0 ? (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getDocumentIcon(doc.document_type)}
                            <div>
                              <p className="font-medium text-gray-900">
                                {getDocumentTypeName(doc.document_type)}
                              </p>
                              <p className="text-sm text-gray-500">
                                อัปโหลดเมื่อ {formatDate(doc.uploaded_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDocumentView(doc)}
                              className="flex items-center"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              ดู
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDocumentDownload(doc)}
                              className="flex items-center"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              ดาวน์โหลด
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>ไม่มีเอกสารแนับ</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rejection Reason */}
            {verification.status === 'rejected' && verification.rejection_reason && (
              <Card className="border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    เหตุผลการปฏิเสธ
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-red-700 bg-white p-4 rounded border border-red-200 leading-relaxed">
                    {verification.rejection_reason}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Timeline & Status */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-orange-600 text-xs">📅</span>
                  </div>
                  ข้อมูลการดำเนินการ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600 font-medium">สถานะปัจจุบัน</Label>
                  <div>{getStatusBadge(verification.status)}</div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600 font-medium">วันที่ส่ง</Label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                    {formatDate(verification.submitted_at)}
                  </p>
                </div>

                {verification.verified_at && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-600 font-medium">วันที่อนุมัติ</Label>
                    <p className="text-green-700 bg-green-50 p-3 rounded border text-sm">
                      {formatDate(verification.verified_at)}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-sm text-gray-600 font-medium">จำนวนเอกสาร</Label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded border text-sm">
                    {verification.document_count} ไฟล์
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ปฏิเสธการยืนยันตัวตน</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reason" className="text-sm font-medium">
                เหตุผลในการปฏิเสธ *
              </Label>
              <Textarea
                id="reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="กรุณาระบุเหตุผลในการปฏิเสธ..."
                className="mt-1"
                rows={4}
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowRejectDialog(false)}
              disabled={!!actionLoading}
            >
              ยกเลิก
            </Button>
            <Button 
              onClick={handleReject}
              disabled={!rejectionReason.trim() || !!actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading === 'reject' ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent mr-2"></div>
              ) : null}
              ปฏิเสธ
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}